
/**
 * Utility functions for handling video embeds
 */

/**
 * Normalizes YouTube video IDs from various URL formats
 * @param input YouTube URL or video ID
 * @returns Normalized YouTube video ID
 */
export const normalizeYoutubeId = (input: string): string => {
  // Handle various YouTube URL formats
  if (input.includes('youtu.be/')) {
    return input.split('youtu.be/')[1].split('?')[0];
  }
  
  if (input.includes('youtube.com/watch')) {
    const urlParams = new URLSearchParams(input.split('?')[1]);
    return urlParams.get('v') || '';
  }
  
  if (input.includes('youtube.com/embed/')) {
    return input.split('youtube.com/embed/')[1].split('?')[0];
  }
  
  // Assume it's already a video ID
  return input;
};

/**
 * Normalizes Vimeo video IDs from various URL formats
 * @param input Vimeo URL or video ID
 * @returns Normalized Vimeo video ID
 */
export const normalizeVimeoId = (input: string): string => {
  // Handle Vimeo URL formats
  if (input.includes('vimeo.com/')) {
    const parts = input.split('/');
    return parts[parts.length - 1].split('?')[0];
  }
  
  if (input.includes('player.vimeo.com/video/')) {
    const parts = input.split('video/');
    return parts[parts.length - 1].split('?')[0];
  }
  
  // Assume it's already a video ID
  return input;
};

/**
 * Gets YouTube embed URL from video ID
 * @param videoId YouTube video ID
 * @returns YouTube embed URL
 */
export const getYoutubeEmbedUrl = (videoId: string): string => {
  const normalizedId = normalizeYoutubeId(videoId);
  return `https://www.youtube.com/embed/${normalizedId}?autoplay=0&modestbranding=1&rel=0`;
};

/**
 * Gets Vimeo embed URL from video ID
 * @param videoId Vimeo video ID
 * @returns Vimeo embed URL
 */
export const getVimeoEmbedUrl = (videoId: string): string => {
  const normalizedId = normalizeVimeoId(videoId);
  return `https://player.vimeo.com/video/${normalizedId}?autoplay=0&title=0&byline=0&portrait=0`;
};
